import { Link } from "react-router";
import { Link2, TriangleAlert } from "lucide-react";
import { useSession } from "@/lib/store/session";
import { useUser } from "@/lib/query/useUser";
import { useActivities } from "@/lib/query/useActivities";
import { oldestActivity } from "@/lib/domain/activities";
import { Spinner } from "@/components/ui/spinner";

function ImportingBanner() {
  const { activities } = useActivities();
  const oldest = oldestActivity(activities);
  const date = oldest ? oldest.startDate.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : null;
  return (
    <div role="status" aria-live="polite" className="flex items-center gap-3 border-b border-accent/20 bg-accent/10 px-4 py-2 text-sm text-fg tab:px-6">
      <Spinner size="sm" className="text-accent" label="Importing" />
      <span>
        Importing your Garmin history
        {date ? (
          <>
            {" "}
            — imported back to <span className="num font-semibold">{date}</span> so far
          </>
        ) : (
          "…"
        )}
      </span>
      <Link
        to="/upload"
        className="ml-auto shrink-0 text-xs text-fg-muted underline-offset-2 hover:text-fg hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/50"
      >
        Older runs? Upload files
      </Link>
    </div>
  );
}

/**
 * Why an import stopped, in the user's words. Keys are the values the API puts
 * in `synchError` (server: BackfillFailure); anything unrecognised falls back
 * to the generic line rather than showing a raw code.
 */
const BACKFILL_FAILURE_COPY: Record<string, string> = {
  "not-permitted":
    "Garmin wouldn't share your past activities with us. New runs will still sync automatically.",
  "auth-failed":
    "We hit a problem talking to Garmin while importing your history. We'll keep retrying automatically.",
};

function ImportFailedBanner({ reason }: { reason?: string | null }) {
  const detail =
    (reason && BACKFILL_FAILURE_COPY[reason]) ||
    "We couldn't import your Garmin history. New runs will still sync automatically.";
  return (
    <div role="status" aria-live="polite" className="flex items-center gap-3 border-b border-warning/20 bg-warning/10 px-4 py-2 text-sm text-fg tab:px-6">
      <TriangleAlert className="size-4 shrink-0 text-warning" aria-hidden />
      <span>{detail}</span>
      <Link
        to="/upload"
        className="ml-auto shrink-0 text-xs text-fg-muted underline-offset-2 hover:text-fg hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/50"
      >
        Upload your files instead
      </Link>
    </div>
  );
}

export function SyncBanner() {
  const loggedIn = useSession((s) => !!s.userId);
  const { data: user } = useUser();
  if (!loggedIn || !user) return null;
  // Order matters: a failed import is also a finished one, so this must be
  // checked before the "still importing" case (#274).
  if (user.synchFailed && user.garminSet) return <ImportFailedBanner reason={user.synchError} />;
  if (user.synchComplete === false && user.garminSet) return <ImportingBanner />;
  // garminReconnect = the user HAD Garmin and lost it (a dead OAuth2 refresh
  // token clears every credential column, #285) — as opposed to never having
  // connected, which gets the quiet link below. synchSet is NOT the signal:
  // the "I'll upload files instead" path sets it too.
  if (user.garminReconnect) {
    return (
      <div role="status" aria-live="polite" className="flex items-center gap-3 border-b border-warning/20 bg-warning/10 px-4 py-2 text-sm text-fg tab:px-6">
        <TriangleAlert className="size-4 shrink-0 text-warning" aria-hidden />
        <span>Garmin needs reconnecting — new runs are not syncing.</span>
        <Link
          to="/onboarding/synch"
          className="ml-auto shrink-0 text-xs text-fg-muted underline-offset-2 hover:text-fg hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/50"
        >
          Reconnect Garmin
        </Link>
      </div>
    );
  }
  if (user.garminSet === false) {
    return (
      <div className="flex items-center gap-2 border-b border-border bg-surface-2/60 px-4 py-1.5 text-xs text-fg-muted tab:px-6">
        <Link2 className="size-3.5 shrink-0" aria-hidden />
        <Link to="/onboarding/synch" className="underline-offset-2 hover:text-fg hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/50">
          Connect Garmin to sync activities automatically
        </Link>
      </div>
    );
  }
  return null;
}
